import React, { useEffect } from 'react';
import { CheckCircle2, Camera, Clock, Zap, Download, MoveRight, ShieldCheck } from 'lucide-react';

// Onboarding hub for Sanchit Kedia / TFB EduVest LLP. Signed 2026-09-16.
// Kept deliberately short: he signed, he asked three things, and the only one
// blocking him is the studio brief. Everything else is one line or cut.

const AGREEMENT = '/assets/sanchit/agreement.pdf';
const CONTENT_OS = 'https://www.notion.so/3de3ac2a53098196a3a7d80cf741a91f';
const QUESTIONNAIRE = 'https://docs.google.com/document/d/18YpAPzknCm4xdqTT5jNTG7eT2TeibU8MpizaZHegOwk/edit';
const SESSION_EXAMPLE = 'https://drive.google.com/drive/folders/1sh4_G0gAFi9UO2rcarQk1JcNCrfkt8L9?usp=sharing';

const BLOCKS = [
    { n: '1', t: 'Three videos on the studio backdrops', time: 'use whatever sets they have', d: 'Different backdrop each time if they can offer it. Change your outfit for every single video.' },
    { n: '2', t: 'Two videos on green screen', time: 'same session', d: 'So we can drop you into any background later without booking you again. Outfit change for these too.' },
    { n: '3', t: 'Portrait stills', time: '20 to 30 frames', d: 'Neutral, smiling, serious. Shot in the same session, becomes your thumbnails and covers.' },
];

const DELIVERY = [
    'Speak as you go. Do not think the sentence through and then say it, that is the thing that reads as stiff on camera.',
    'At the end of every sentence, look straight down the lens and pause before you start the next one.',
    'Keep your hands below your chest. Never in front of your face or mouth.',
    'Natural beats polished here. The more normally you talk, the better the clone comes out.',
];

const SPEC = [
    ['Length', '30 to 60 minutes of recording. Closer to 30 if you move quickly, and five videos total.'],
    ['Camera', '4K, 25 or 30fps, with enough headroom and side space to crop to vertical cleanly.'],
    ['Light', 'Standard studio lighting, soft and even on the face. Same setup across every video.'],
    ['Background', 'Three on their own backdrops, two on green screen.'],
    ['Wardrobe', 'A different outfit for every video, so five. Solid colours, no fine stripes or big logos, they shimmer on camera.'],
    ['Audio', 'Lav plus boom to 48kHz WAV, not just the camera track. AC off during takes.'],
    ['Files', 'Original uncompressed files to a Drive folder. Not edited, not graded, not over WhatsApp.'],
];

const STEPS = [
    { t: 'Agreement signed', d: 'Signed and returned. Reference copy below.', done: true, link: AGREEMENT },
    { t: 'Fill the questionnaire', d: 'Seventeen questions, around twenty minutes, and the most useful twenty minutes you can give us. Where we already have an answer from your call it is written in for you, so correct it rather than retyping. The positioning and every script get built from this.', link: QUESTIONNAIRE, linkLabel: 'Open the questionnaire' },
    { t: 'Book the studio', d: 'Forward the brief above, then send us the date. Passages reach you 48 hours before.' },
    { t: 'Send your consent for the clone', d: 'A short written line authorising us to build and use your AI avatar and voice. This is for our use only, it is not a consent to upload you anywhere. Platform level consent for the voice tools is a separate call once your clone is training.' },
    { t: 'Record 2 hours of audio at home', d: 'Four sessions of about 30 minutes, not one long sitting. Read a book, or just talk through what you do all day. Quiet room, phone voice memo at best quality is fine. The more normally you speak, the better the clone gets.' },
    { t: 'Record the pronunciation list', d: 'Once your audio reaches us we send a short list of the words that keep coming up in your world, finance, investment, interest and the rest. You read them out, send them back, done.' },
    { t: 'Open your Content OS', d: 'Your Notion workspace. Ideas land as blocks, you comment or approve, then it moves through scripting, recording with your clone, editing, your review, and posting. Open it and request access, or send Suresh the email you want on it.', link: CONTENT_OS, linkLabel: 'Content OS' },
];

const PHASES = [
    ['Now to 30 Sep 2026', 'Shoot, clone built and approved by you, positioning and first scripts ready.'],
    ['October', 'Live on the 1st, your 90 days starts here. Minimum 30 videos a month.'],
    ['November', 'What works gets volume. Comment to DM automation switches on.'],
    ['December', 'Guarantee lands: 5,000 new followers within 90 days of your first video, or 100% of fees back.'],
];

export default function SanchitOnboarding() {
    useEffect(() => {
        document.title = 'Onboarding | Sanchit Kedia via SocialSEO';
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="font-sans leading-relaxed text-white overflow-x-hidden selection:bg-indigo-500/30 pb-20 pt-24 relative z-10 min-h-screen">
            <div className="fixed inset-0 min-h-screen bg-[#080B11] -z-20 overflow-hidden">
                <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] bg-indigo-600/20 blur-[160px] rounded-full mix-blend-screen pointer-events-none" />
                <div className="absolute bottom-[-20%] right-[-10%] w-[60%] h-[60%] bg-emerald-600/15 blur-[160px] rounded-full mix-blend-screen pointer-events-none" />
            </div>

            <header className="fixed top-0 left-0 right-0 z-50 p-5 bg-[#07090E]/80 backdrop-blur-lg border-b border-white/5">
                <div className="flex items-center gap-2 w-full max-w-3xl mx-auto">
                    <span className="font-bold tracking-tight text-white text-lg">Social<span className="text-indigo-400">SEO</span></span>
                    <span className="text-white/20 mx-1">/</span>
                    <span className="text-white/70 font-medium text-sm truncate">Sanchit Kedia</span>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-5 pt-8 space-y-14">

                {/* Hero */}
                <section>
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[11px] font-bold tracking-wide uppercase mb-5">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Signed
                    </div>
                    <h1 className="text-4xl sm:text-5xl font-black tracking-tight mb-5 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400 leading-[1.05] pb-1">
                        Signed. Here is<br />what happens now.
                    </h1>
                    <p className="text-white/70 text-base sm:text-lg leading-relaxed">
                        Hey Sanchit, two things from you: fill the questionnaire and book the studio. Everything else is ours.
                        You record once, then you are off camera for good.
                    </p>
                </section>

                {/* Studio brief */}
                <section>
                    <h2 className="text-xl font-bold mb-2 flex items-center gap-2.5">
                        <Camera className="text-emerald-400 w-5 h-5 shrink-0" /> The studio brief
                    </h2>
                    <p className="text-slate-400 text-sm mb-4">
                        Forward this to the studio. 15 to 25 minutes of usable footage, one session, never again.
                    </p>
                    <a
                        href={SESSION_EXAMPLE}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 mb-6 px-4 py-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/25 text-emerald-300 font-semibold text-sm hover:bg-emerald-500/20 hover:text-white transition-colors"
                    >
                        <Camera className="w-4 h-4" /> See a finished session <MoveRight className="w-4 h-4" />
                    </a>
                    <p className="text-slate-500 text-xs leading-relaxed mb-6 -mt-2">
                        That folder is a real session we ran, so you and the studio can see exactly what lands with us
                        rather than working off a description.
                    </p>

                    <div className="space-y-2.5 mb-6">
                        {BLOCKS.map((b) => (
                            <div key={b.n} className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-start gap-3.5">
                                <div className="shrink-0 w-8 h-8 rounded-lg bg-emerald-500/15 text-emerald-400 flex items-center justify-center font-black text-sm">{b.n}</div>
                                <div className="min-w-0">
                                    <div className="flex flex-wrap items-baseline gap-x-2.5 mb-1">
                                        <h3 className="font-bold text-white text-[15px]">{b.t}</h3>
                                        <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-400/80">{b.time}</span>
                                    </div>
                                    <p className="text-slate-400 text-sm leading-relaxed">{b.d}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="bg-indigo-500/[0.07] border border-indigo-500/25 rounded-xl p-5 mb-6">
                        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-indigo-300 mb-3">How to talk on the day</div>
                        <ul className="space-y-2.5">
                            {DELIVERY.map((d, i) => (
                                <li key={i} className="flex gap-2.5 text-slate-300 text-sm leading-relaxed">
                                    <Zap className="w-4 h-4 text-indigo-400 shrink-0 mt-1" />
                                    <span>{d}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="bg-white/5 border border-white/10 rounded-xl divide-y divide-white/5 overflow-hidden">
                        {SPEC.map(([k, v]) => (
                            <div key={k} className="px-4 py-3 sm:flex sm:gap-5">
                                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-indigo-300/80 sm:w-24 sm:shrink-0 mb-1 sm:mb-0 sm:pt-0.5">{k}</div>
                                <div className="text-slate-400 text-sm leading-relaxed">{v}</div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Checklist */}
                <section>
                    <h2 className="text-xl font-bold mb-5 flex items-center gap-2.5">
                        <CheckCircle2 className="text-indigo-400 w-5 h-5 shrink-0" /> Your checklist
                    </h2>
                    <div className="space-y-2.5">
                        {STEPS.map((s, i) => (
                            <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-start gap-3.5">
                                <div className={`shrink-0 w-7 h-7 rounded-full text-[11px] font-bold flex items-center justify-center ${s.done ? 'bg-emerald-500 text-white' : 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30'}`}>
                                    {s.done ? '✓' : i + 1}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <h3 className="font-bold text-white text-[15px] mb-1">{s.t}</h3>
                                    <p className="text-slate-400 text-sm leading-relaxed">{s.d}</p>
                                    {s.link && (
                                        <a
                                            href={s.link}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="inline-flex items-center gap-2 mt-3 px-3.5 py-2 rounded-lg bg-indigo-500/10 border border-indigo-500/25 text-indigo-300 font-semibold text-xs hover:bg-indigo-500/20 hover:text-white transition-colors"
                                        >
                                            <Download className="w-3.5 h-3.5" /> {s.linkLabel || 'Agreement'} <MoveRight className="w-3.5 h-3.5" />
                                        </a>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Timeline */}
                <section>
                    <h2 className="text-xl font-bold mb-5 flex items-center gap-2.5">
                        <Clock className="text-indigo-400 w-5 h-5 shrink-0" /> The three months
                    </h2>
                    <div className="space-y-2.5">
                        {PHASES.map(([w, d], i) => (
                            <div key={i} className="flex items-start gap-3.5 bg-white/5 border border-white/10 rounded-xl p-4">
                                <Zap className="w-4 h-4 text-indigo-400 shrink-0 mt-1" />
                                <div className="min-w-0">
                                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-indigo-400 mb-1">{w}</div>
                                    <p className="text-slate-300 text-sm leading-relaxed">{d}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                    <p className="text-slate-500 text-xs leading-relaxed mt-4">
                        Term runs 1 October to 31 December 2026, measured from your first video, so onboarding sits outside
                        your billing window. Every video carries two rounds of revisions and nothing publishes without your approval.
                    </p>
                </section>

                {/* Closing */}
                <section>
                    <div className="bg-gradient-to-br from-slate-900 to-[#0F172A] border border-white/10 rounded-2xl p-6 text-center">
                        <ShieldCheck className="w-7 h-7 text-emerald-400 mx-auto mb-3" />
                        <p className="text-white/60 text-sm mb-3 max-w-lg mx-auto leading-relaxed">
                            One thing to expect when you see the first clone video: you will spot that it is a clone.
                            Everyone does with their own face. Almost nobody else will. Judge it as a stranger would.
                        </p>
                        <p className="text-white/60 text-sm mb-3">One rule of ours: your nephew stays in the story, never on camera.</p>
                        <p className="font-bold text-white">You record once. We build the rest.</p>
                    </div>
                </section>

            </main>
        </div>
    );
}
